import { useCallback, useContext, useEffect, useMemo, useRef } from "react";

import {
  type Options as ReactHotkeyOptions,
  useHotkeys as useReactHotkey,
} from "react-hotkeys-hook";

import { FocusContext } from "../Focus/FocusContext";
import { addHotkeyToRegistry } from "./hotkey.registry";
import { type AvailableTags } from "./hotkey.types";

interface IUseHotkeyArgs {
  command: string;
  /** Label shown for this command in the ctrl+/ overlay. Omit to keep it out of the overlay. */
  friendlyName?: string | undefined;
  onCommand?: (() => void) | undefined;
  triggerBehavior?: "press" | "up" | "down" | undefined;
  disabled?: boolean | undefined;
  preventDefaultWhenDisabled?: boolean | undefined;
  supportedTags?: AvailableTags[] | undefined;
  /**
   * Fire regardless of which focus layer is current. Only for keys that are
   * genuinely global — the ctrl+/ hotkey overlay is the motivating case, since it
   * must open from anywhere, including above a modal that has stolen focus.
   * Everything else should stay scoped to its focus layer.
   * @default false
   */
  ignoreFocusContext?: boolean | undefined;
}

export function useHotkey({
  command,
  friendlyName,
  onCommand,
  supportedTags,
  triggerBehavior = "press",
  preventDefaultWhenDisabled = true,
  disabled = false,
  ignoreFocusContext = false,
}: IUseHotkeyArgs) {
  const commandWrapper = useCallback(
    (e: KeyboardEvent) => {
      if (!disabled || preventDefaultWhenDisabled) {
        e?.preventDefault();
        e?.stopPropagation();
      }
      if (!disabled && !e.repeat) {
        onCommand?.();
      }
    },
    [disabled, preventDefaultWhenDisabled, onCommand],
  );

  const commandWrapperStaticRef = useRef(commandWrapper);
  useEffect(() => {
    commandWrapperStaticRef.current = commandWrapper;
  }, [commandWrapper]);

  const focusContext = useContext(FocusContext);
  const isFocusActive =
    ignoreFocusContext || (focusContext?.isCurrentFocus ?? false);

  // Publish to the registry the ctrl+/ overlay reads. The conditions mirror when the key
  // actually fires — a disabled key, or one on a focus layer that isn't current, must not
  // be advertised as available. An unnamed command has nothing to label a row with.
  const listedName = !disabled && isFocusActive ? friendlyName : undefined;
  useEffect(() => {
    if (!listedName) {
      return undefined;
    }
    return addHotkeyToRegistry({ command, friendlyName: listedName });
  }, [command, listedName]);

  const commandOptions = useMemo<ReactHotkeyOptions>(() => {
    const keydown =
      triggerBehavior === "down"
        ? true
        : triggerBehavior !== "up"
          ? undefined
          : false;
    const keyup =
      triggerBehavior === "up"
        ? true
        : triggerBehavior !== "down"
          ? undefined
          : false;
    const options: ReactHotkeyOptions = {
      filter: () => isFocusActive,
      filterPreventDefault: false,
      enableOnTags: supportedTags ?? ["INPUT", "SELECT", "TEXTAREA"],
      enableOnContentEditable: true,
      // Only set keydown/keyup when defined — exactOptionalPropertyTypes forbids
      // assigning `undefined` to these optional react-hotkeys-hook options.
      ...(keydown !== undefined && { keydown }),
      ...(keyup !== undefined && { keyup }),
    };
    return options;
  }, [supportedTags, isFocusActive, triggerBehavior]);

  useReactHotkey(
    command,
    (e) => commandWrapperStaticRef.current(e),
    commandOptions,
  );
}
